import React from "react";
import { useForm, useFieldArray } from "react-hook-form";

export default function SuburbEdit({
  initialData = {},
  onSubmit,
  isLoading = false,
}) {
  const isEdit = !!initialData._id;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      suburb: initialData.suburb || "",
      postcode: initialData.postcode || "",
      reaPrice: initialData.reaPrice || "",
      domainPrice:
        initialData.domainPrice?.length > 0
          ? initialData.domainPrice.map((d) => ({
              minPrice: d.minPrice ?? "",
              maxPrice: d.maxPrice ?? "",
              fee: d.fee ?? "",
            }))
          : [{ minPrice: "", maxPrice: "", fee: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "domainPrice",
  });

  const submitForm = (data) => {
    const transformed = {
      ...data,
      reaPrice: data.reaPrice ? String(data.reaPrice) : null,
      domainPrice: data.domainPrice
        .filter((d) => d.fee !== "" && d.fee !== undefined)
        .map((d) => ({
          minPrice: Number(d.minPrice),
          maxPrice: Number(d.maxPrice),
          fee: Number(d.fee),
        })),
    };
    onSubmit(transformed);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
      {/* Suburb & Postcode */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="form-label">Suburb Name *</label>
          <input
            type="text"
            {...register("suburb", { required: "Suburb name is required" })}
            placeholder="e.g., Peakhurst"
            disabled={isEdit}
            className={`form-input border ${
              errors.suburb ? "border-red-500" : "border-mediumgray"
            } ${isEdit ? "bg-lightgray cursor-not-allowed" : ""}`}
          />
          {isEdit && (
            <p className="text-xs text-darkgray">
              Cannot be changed after creation.
            </p>
          )}
          {errors.suburb && (
            <p className="form-error-message">{errors.suburb.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="form-label">Postcode *</label>
          <input
            type="text"
            {...register("postcode", {
              required: "Postcode is required",
              pattern: {
                value: /^\d{4}$/,
                message: "Must be a 4-digit postcode",
              },
            })}
            placeholder="e.g., 2210"
            disabled={isEdit}
            className={`form-input border ${
              errors.postcode ? "border-red-500" : "border-mediumgray"
            } ${isEdit ? "bg-lightgray cursor-not-allowed" : ""}`}
          />
          {isEdit && (
            <p className="text-xs text-darkgray">
              Cannot be changed after creation.
            </p>
          )}
          {errors.postcode && (
            <p className="form-error-message">{errors.postcode.message}</p>
          )}
        </div>
      </div>

      {/* REA Price */}
      <div className="space-y-2">
        <label className="form-label">REA Price</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-darkgray font-medium">
            $
          </span>
          <input
            type="number"
            step="1"
            {...register("reaPrice")}
            placeholder="e.g., 1079"
            className={`form-input border pl-7 ${
              errors.reaPrice ? "border-red-500" : "border-mediumgray"
            }`}
          />
        </div>
      </div>

      {/* Domain Price Tiers */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="form-label mb-0">Domain Price Tiers</label>
          <button
            type="button"
            onClick={() => append({ minPrice: "", maxPrice: "", fee: "" })}
            className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
          >
            + Add Tier
          </button>
        </div>

        {fields.map((field, index) => (
          <div
            key={field.id}
            className="border border-mediumgray rounded-lg p-4 bg-lightergray"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Tier {index + 1}</span>
              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Remove
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { name: "minPrice", label: "Min Price ($)", placeholder: "0" },
                {
                  name: "maxPrice",
                  label: "Max Price ($)",
                  placeholder: "999999",
                },
                { name: "fee", label: "Fee ($)", placeholder: "1815" },
              ].map(({ name, label, placeholder }) => (
                <div key={name} className="space-y-1">
                  <label className="text-xs font-medium text-darkgray">
                    {label}
                  </label>
                  <input
                    type="number"
                    {...register(`domainPrice.${index}.${name}`, {
                      required: "Required",
                      min: { value: 0, message: "Min 0" },
                    })}
                    placeholder={placeholder}
                    className={`form-input border text-sm ${
                      errors.domainPrice?.[index]?.[name]
                        ? "border-red-500"
                        : "border-mediumgray"
                    }`}
                  />
                  {errors.domainPrice?.[index]?.[name] && (
                    <p className="text-xs text-red-500">
                      {errors.domainPrice[index][name].message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-6">
        <button type="submit" className="black-button" disabled={isLoading}>
          {isLoading ? "Creating..." : isEdit ? "Update Suburb" : "Create Suburb"}
        </button>
      </div>
    </form>
  );
}